/**
 * 登录页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Divider,
  message,
  Space,
  Row,
  Col,
  Modal,
} from 'antd';
import {
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  KeyOutlined,
  ReloadOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useStore } from '../../contexts/StoreContext';
import { authApi } from '../../api/index';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

// 检测是否为移动设备
const isMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { storeName, logoUrl } = useStore();
  const [form] = Form.useForm();

  // 检查是否是移动端后台登录
  const isMobileAdmin = location.pathname.startsWith('/admin/m');
  const mobileDevice = isMobile();

  // 登录请求加载态
  const [loading, setLoading] = useState(false);
  // 是否需要 OTP 验证（管理员）
  const [requiresOtp, setRequiresOtp] = useState(false);
  // OTP 加载态
  const [otpLoading, setOtpLoading] = useState(false);
  // OTP 倒计时
  const [otpCountdown, setOtpCountdown] = useState(0);
  // 图像验证码会话 ID
  const [captchaSessionId, setCaptchaSessionId] = useState('');
  // 图像验证码 Base64
  const [captchaImage, setCaptchaImage] = useState('');
  // 验证码加载态
  const [captchaLoading, setCaptchaLoading] = useState(false);
  // 首次访问提示弹窗
  const [welcomeModalVisible, setWelcomeModalVisible] = useState(false);

  // 生成验证码会话 ID
  const generateSessionId = () => {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  };

  // 加载图像验证码
  const loadCaptchaImage = async () => {
    const sessionId = generateSessionId();
    setCaptchaSessionId(sessionId);
    setCaptchaLoading(true);
    try {
      const result = await authApi.getCaptchaImage(sessionId);
      setCaptchaImage(result.imageBase64);
    } catch (err) {
      message.error('加载验证码失败');
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 初始加载验证码
  useEffect(() => {
    loadCaptchaImage();
  }, []);

  // 首次访问提示弹窗
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('yk2_login_welcome_seen');
    if (!hasSeenWelcome) {
      setWelcomeModalVisible(true);
    }
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // 检查是否需要 OTP 验证
  const checkOtpRequired = async (email) => {
    try {
      const result = await authApi.checkOtpRequired(email);
      setRequiresOtp(result.requiresOtp);
      // 切换验证类型时清空对应的验证码字段
      form.setFieldsValue({ captcha: undefined, otp: undefined });
    } catch (err) {
      setRequiresOtp(false);
    }
  };

  // 发送 OTP（管理员）
  const handleSendOtp = async () => {
    const email = form.getFieldValue('email');
    if (!email) {
      message.warning('请先输入邮箱');
      form.setFields([{ name: 'email', errors: ['请先输入邮箱'] }]);
      return;
    }

    setOtpLoading(true);
    try {
      await authApi.sendOtp(email);
      message.success('OTP 已发送至管理员邮箱，有效期 10 分钟');
      setOtpCountdown(60); // 60 秒倒计时
    } catch (err) {
      message.error(err?.response?.data?.message || '发送失败');
    } finally {
      setOtpLoading(false);
    }
  };

  // 关闭欢迎弹窗
  const handleWelcomeModalClose = () => {
    setWelcomeModalVisible(false);
    localStorage.setItem('yk2_login_welcome_seen', 'true');
  };

  /**
   * 提交表单：调用 login()，根据用户角色跳转页面
   * @param {Object} values - { email, password, captcha, otp }
   */
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        password: values.password,
      };

      // 管理员需要 OTP，普通用户需要图像验证码
      if (requiresOtp) {
        payload.otp = values.otp;
      } else {
        payload.captchaSessionId = captchaSessionId;
        payload.captcha = values.captcha;
      }

      const user = await login(payload);
      message.success('登录成功！');
      // 管理员根据设备类型跳转：移动端跳转 /admin/m，桌面端跳转 /admin
      if (user?.role === 'admin') {
        if (isMobile()) {
          navigate('/admin/m');
        } else {
          navigate('/admin');
        }
      } else {
        navigate('/');
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || '登录失败，请重试';

      // 验证码错误时刷新验证码
      if (errorMsg.includes('验证码')) {
        loadCaptchaImage();
        form.setFieldsValue({ captcha: undefined });
      }

      // 展示错误信息
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    // 全屏居中布局
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}08 100%)`,
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* 店铺品牌标识 */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={storeName}
              style={{ maxHeight: 80, maxWidth: '100%', marginBottom: 8 }}
            />
          ) : (
            <Title
              level={2}
              style={{
                color: COLORS.ink,
                margin: 0,
                letterSpacing: '0.3em',
                fontWeight: 300,
                fontSize: 28,
              }}
            >
              {storeName}
            </Title>
          )}
          {isMobileAdmin && mobileDevice && (
            <p style={{ color: COLORS.lightInk, fontSize: 13, margin: '12px 0 0', letterSpacing: '0.1em' }}>
              移动端管理后台
            </p>
          )}
          <div style={{
            width: 40,
            height: 2,
            background: COLORS.cinnabar,
            margin: '16px auto 0',
          }} />
        </div>

        <Card
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: 'none',
          }}
          bodyStyle={{ padding: '32px 32px 24px', background: COLORS.paper }}
        >
          <Title
            level={3}
            style={{
              textAlign: 'center',
              marginBottom: 28,
              color: COLORS.ink,
              fontSize: 20,
              letterSpacing: '0.15em',
              fontWeight: 400,
            }}
          >
            账户登录
          </Title>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="large"
          >
            {/* 邮箱字段 */}
            <Form.Item
              name="email"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>邮箱</span>}
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请输入邮箱"
                autoComplete="email"
                onBlur={(e) => checkOtpRequired(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            {/* 密码字段 */}
            <Form.Item
              name="password"
              label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>密码</span>}
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: COLORS.bambooGreen }} />}
                placeholder="请输入密码"
                autoComplete="current-password"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>

            {/* 图像验证码字段（普通用户） */}
            {!requiresOtp && (
              <Form.Item
                name="captcha"
                label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>验证码</span>}
                rules={[
                  { required: true, message: '请输入验证码' },
                  { len: 4, message: '验证码为 4 位' },
                ]}
              >
                <Row gutter={8}>
                  <Col span={10}>
                    <Input
                      prefix={<SafetyOutlined style={{ color: COLORS.bambooGreen }} />}
                      placeholder="输入验证码"
                      maxLength={10}
                      autoComplete="off"
                      size="large"
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                  <Col span={14}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      height: '40px'
                    }}>
                      {captchaImage && (
                        <img
                          src={`data:image/png;base64,${captchaImage}`}
                          alt="验证码"
                          style={{
                            height: 40,
                            borderRadius: 4,
                            cursor: 'pointer',
                            border: `1px solid ${COLORS.lightBorder}`
                          }}
                          onClick={loadCaptchaImage}
                          title="点击刷新验证码"
                        />
                      )}
                      <Button
                        icon={<ReloadOutlined style={{ color: COLORS.teaBrown }} />}
                        onClick={loadCaptchaImage}
                        loading={captchaLoading}
                        size="large"
                        style={{ height: 40, borderRadius: 8, borderColor: COLORS.lightBorder }}
                      />
                    </div>
                  </Col>
                </Row>
              </Form.Item>
            )}

            {/* OTP 字段（管理员 - 6 位） */}
            {requiresOtp && (
              <Form.Item
                name="otp"
                label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>OTP 验证码</span>}
                rules={[
                  { required: true, message: '请输入 OTP 验证码' },
                  { len: 6, message: 'OTP 为 6 位数字' },
                ]}
              >
                <Row gutter={8}>
                  <Col span={12}>
                    <Input
                      prefix={<KeyOutlined style={{ color: COLORS.bambooGreen }} />}
                      placeholder="6 位 OTP"
                      maxLength={6}
                      style={{ borderRadius: 8 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      onClick={handleSendOtp}
                      loading={otpLoading}
                      disabled={otpCountdown > 0}
                      style={{ borderRadius: 8, background: COLORS.teaBrown, borderColor: COLORS.teaBrown }}
                    >
                      {otpCountdown > 0 ? `${otpCountdown}秒` : '发送 OTP'}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            )}

            {/* 登录按钮 */}
            <Form.Item style={{ marginBottom: 12, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{
                  height: 48,
                  fontSize: 16,
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                登录
              </Button>
            </Form.Item>

            {/* 忘记密码 */}
            <Form.Item style={{ marginBottom: 0 }}>
              <div style={{ textAlign: 'right' }}>
                <Link to="/forgot-password">
                  <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>忘记密码？</Text>
                </Link>
              </div>
            </Form.Item>
          </Form>

          <Divider plain style={{ borderColor: `${COLORS.teaBrown}15` }}>
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>或者</Text>
          </Divider>

          {/* 注册引导 */}
          <div style={{ textAlign: 'center' }}>
            <Space>
              <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>还没有账户？</Text>
              <Link to="/register">
                <Text style={{ color: COLORS.cinnabar, fontWeight: 500, fontSize: 13 }}>立即注册</Text>
              </Link>
            </Space>
          </div>
        </Card>

        {/* 返回首页 */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Link to="/">
            <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>← 返回首页</Text>
          </Link>
        </div>
      </div>

      {/* 首次访问欢迎弹窗 */}
      <Modal
        open={welcomeModalVisible}
        onCancel={handleWelcomeModalClose}
        footer={[
          <Button key="ok" type="primary" onClick={handleWelcomeModalClose} style={{ background: COLORS.teaBrown, borderColor: COLORS.teaBrown }}>
            我知道了
          </Button>,
        ]}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <SmileOutlined style={{ fontSize: 48, color: COLORS.bambooGreen, marginBottom: 16 }} />
          <Title level={4} style={{ color: COLORS.ink, marginBottom: 16 }}>
            欢迎来到 {storeName || '悦来悦好'}
          </Title>
          <Text style={{ color: COLORS.lightInk, fontSize: 14, lineHeight: 1.8 }}>
            如果您是我们的老客户，<br />
            请用密码 <strong style={{ color: COLORS.cinnabar }}>123456</strong> 登录，<br />
            登录进去后记得改密码哦~
          </Text>
        </div>
      </Modal>
    </div>
  );
}
